import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useIntl} from 'react-intl'
import {useDispatch, useSelector} from 'react-redux'
import {buildDataId} from 'components/Filters/utils'
import useShowOverlay from 'hooks/useShowOverlay/useShowOverlay'
import {getCurrencyCode} from 'modules/meta/selectors'
import {getPriceTrendBottomSheetVisibility} from 'modules/overlay/selectors'
import {hideOverlay} from 'modules/overlay/slice'
import {OverlayType} from 'modules/overlay/types'
import {getHotelResult} from 'modules/searchApi/selectors'
import {getSearchBoxValues} from 'modules/searchBox/selectors'
import {
  getPriceInsightsTooltipDismissed,
  setPriceInsightsTooltipDismissed
} from 'utils/persistence'

import {Divider} from '@vio/atlas/Divider'
import {Box} from '@vio/atlas/helpers/Box'
import {Icon} from '@vio/atlas/Icon'
import {ButtonLink} from '@vio/atlas/src/components/designSystem/ButtonLink'
import {Text} from '@vio/atlas/Text'
import {useInView} from '@vio/core/src/_web/utils/browser/hooks/useInView'
import {TeamContextProvider} from '@vio/core/src/analytics/components/TeamContext'
import {Team} from '@vio/core/src/analytics/types/Events'
import {FormattedMessageWrapper} from '@vio/core/src/localization/components/FormattedMessage'

import {PriceInsightsTooltip} from './components/PriceInsightsTooltip'
import {PriceTrendInfoCard} from './components/PriceTrendInfoCard'
import {PriceTrendOfferButton} from './components/PriceTrendOfferButton'
import {PriceTrendSidePanel} from './components/PriceTrendSidePanel'
import {usePriceInsightsTracking} from './hooks/usePriceInsightsTracking'
import {usePriceTrend} from './hooks/usePriceTrend'
import {useShouldShowPriceInsightsForFilters} from './hooks/useShouldShowPriceInsightsForFilters'
import {PriceTrendDesktopLoading} from './PriceTrendDesktopLoading'
import {PriceTrendDesktopProps} from './types'
import {PriceTrendMessageKey, priceTrendMessages} from './utils/messages'
import {normalizeFetchParams} from './utils/validation'

export const PriceTrendDesktop = ({
  hotelId,
  insights: insightsProp,
  areOffersLoaded = true,
  onPriceTrendButtonClick
}: PriceTrendDesktopProps) => {
  const {formatMessage} = useIntl()
  const dispatch = useDispatch()
  const showOverlay = useShowOverlay()
  const isSidePanelOpen = useSelector(getPriceTrendBottomSheetVisibility)

  const [isInsightsTooltipVisible, setIsInsightsTooltipVisible] = useState(
    () => !getPriceInsightsTooltipDismissed()
  )

  const dismissInsightsTooltip = useCallback(() => {
    setIsInsightsTooltipVisible(false)
    setPriceInsightsTooltipDismissed()
  }, [])

  // Get search params from Redux store
  const {
    checkIn,
    checkOut,
    rooms: searchBoxRooms
  } = useSelector(getSearchBoxValues)
  const currency = useSelector(getCurrencyCode)

  // Build fetch params internally
  const fetchParams = {
    hotelId,
    checkIn,
    checkOut,
    rooms: searchBoxRooms,
    currency
  }

  const normalizedFetchParams = normalizeFetchParams(fetchParams)

  // Master hook for all price trend data
  const {insights, content, buttonContent, isLoading, isError, selectedOffer} =
    usePriceTrend({
      insights: insightsProp,
      fetchParams: normalizedFetchParams
    })

  const {data: hotel} = useSelector(state => getHotelResult(state, hotelId))

  // Analytics tracking
  const {
    trackSucceeded,
    trackDisplayed,
    trackPreviewViewed,
    trackViewed,
    trackClicked,
    trackCTAClicked,
    trackTooltipClicked,
    trackCarouselDisplayed,
    trackCarouselScrolled,
    trackCarouselHotelClicked,
    trackCarouselSeeMoreClicked,
    insightType,
    actionType,
    isSimilarHotelsAvailable,
    priceInsightsId,
    apLayout
  } = usePriceInsightsTracking({
    hotelId,
    content,
    buttonMessageKey: buttonContent.messageKey,
    offerPrice: buttonContent.priceValue
  })

  const handleBottomSheetViewed = useCallback(
    () => trackViewed('button'),
    [trackViewed]
  )

  const priceTrendsContext = useMemo(
    () => ({
      priceInsightsId,
      insightType,
      actionType,
      apLayout,
      isSimilarHotelsAvailable
    }),
    [
      priceInsightsId,
      insightType,
      actionType,
      apLayout,
      isSimilarHotelsAvailable
    ]
  )

  const {ref: priceTrendRef, inView: isPriceTrendInView} = useInView({
    threshold: 0,
    initialInView: false
  })

  const handlePriceTrendButtonClick = useCallback(
    (messageKey?: PriceTrendMessageKey) => {
      onPriceTrendButtonClick?.(messageKey)
      dispatch(hideOverlay())
    },
    [dispatch, onPriceTrendButtonClick]
  )

  const shouldDisplay =
    !isLoading &&
    (!!insights || !!insightsProp) &&
    areOffersLoaded &&
    !!selectedOffer &&
    !!buttonContent.priceValue &&
    !!content &&
    !isError

  // Track Succeeded — fire once when content is ready and offers are fully loaded
  const hasTrackedSucceededRef = useRef(false)
  useEffect(() => {
    if (shouldDisplay && !hasTrackedSucceededRef.current) {
      hasTrackedSucceededRef.current = true
      trackSucceeded(insightType, actionType, isSimilarHotelsAvailable)
    }
  }, [
    shouldDisplay,
    trackSucceeded,
    insightType,
    actionType,
    isSimilarHotelsAvailable
  ])

  // Track Displayed — fire once when preview content is visible on load
  // trackViewed fires via onViewed when the side panel opens (user actively views full content)
  const hasTrackedDisplayedRef = useRef(false)
  useEffect(() => {
    if (shouldDisplay && !hasTrackedDisplayedRef.current) {
      hasTrackedDisplayedRef.current = true
      trackDisplayed()
    }
  }, [shouldDisplay, trackDisplayed])

  // Track Preview Viewed — fire once when preview content scrolls into view
  const hasTrackedPreviewViewedRef = useRef(false)
  useEffect(() => {
    if (
      isPriceTrendInView &&
      shouldDisplay &&
      !hasTrackedPreviewViewedRef.current
    ) {
      hasTrackedPreviewViewedRef.current = true
      trackPreviewViewed()
    }
  }, [isPriceTrendInView, shouldDisplay, trackPreviewViewed])

  // Hide PriceTrend when a filter bumps a different BOFH offer to cheapest.
  // Redirect (Search API) primary offers ignore room filters, so they stay.
  // Shared with the mobile surfaces — see useShouldShowPriceInsightsForFilters.
  const shouldShowBasedOnFilters = useShouldShowPriceInsightsForFilters(hotelId)

  // Don't render if loading or no insights available
  if (isLoading || (!insights && !insightsProp) || !areOffersLoaded) {
    return <PriceTrendDesktopLoading />
  }

  // Don't render if no offer selected (e.g., when primary offer is split booking)
  // PriceTrend doesn't support split booking offers
  if (!selectedOffer) {
    return null
  }

  // Don't render if sold out (no offer price available)
  if (!buttonContent.priceValue) {
    return null
  }

  if (!content || isError) {
    return null
  }

  const shouldShowPriceTrendButton =
    buttonContent.messageKey &&
    Boolean(buttonContent.priceValue) &&
    Boolean(buttonContent.currency) &&
    Boolean(selectedOffer)

  const recommendationLabel = formatMessage(
    priceTrendMessages[content.recommendation.messageKey],
    content.recommendation.values
  )

  return (
    <TeamContextProvider team={Team.Retention}>
      <div ref={priceTrendRef}>
        <Box
          display={shouldShowBasedOnFilters ? 'block' : 'none'}
          paddingBottom="s800"
        >
          <Box paddingTop="s700" paddingBottom="s700">
            <Divider />
          </Box>
          <Box
            display="flex"
            flexDirection="row"
            gap="s300"
            alignItems="center"
            marginBottom="s500"
            data-id={buildDataId('priceTrend')}
          >
            <Icon name="Sparkles" colorPath="content.brand.primary" />
            <Text variant="titleL">{recommendationLabel}</Text>
          </Box>
          <Box
            display="flex"
            flexDirection="row"
            gap="s400"
            marginBottom="s400"
          >
            {content.similarHotels && (
              <PriceTrendInfoCard
                content={content.similarHotels}
                infoCardName="price-trend-similar-hotels"
                onTooltipClick={trackTooltipClicked}
              />
            )}
            <PriceTrendInfoCard
              content={content.hotelChange}
              infoCardName="price-trend-hotel-change"
              onTooltipClick={trackTooltipClicked}
            />
          </Box>
          <PriceInsightsTooltip
            isOpen={isInsightsTooltipVisible}
            content={formatMessage(priceTrendMessages.insightsTooltip)}
            onClose={dismissInsightsTooltip}
          >
            <ButtonLink
              variant="primary"
              isCompact
              size="lg"
              onClick={e => {
                e.preventDefault()
                dismissInsightsTooltip()
                trackClicked(true)
                showOverlay({type: OverlayType.PriceTrendBottomSheet})
              }}
              href="#"
            >
              <FormattedMessageWrapper
                id="viewMoreInsights"
                defaultMessage="View more insights"
              />
            </ButtonLink>
          </PriceInsightsTooltip>
          {hotel && (
            <PriceTrendSidePanel
              fetchParams={normalizedFetchParams}
              isOpen={isSidePanelOpen}
              onClose={() => {
                trackClicked(false)
                dispatch(hideOverlay())
              }}
              content={content}
              hotel={hotel}
              offerPrice={buttonContent.priceValue}
              nightlyOfferPrice={buttonContent.nightlyPrice}
              recommendationLabel={recommendationLabel}
              onViewed={handleBottomSheetViewed}
              onTooltipClick={trackTooltipClicked}
              onCarouselDisplayed={trackCarouselDisplayed}
              onCarouselScrolled={trackCarouselScrolled}
              onCarouselHotelClicked={trackCarouselHotelClicked}
              onCarouselSeeMoreClicked={trackCarouselSeeMoreClicked}
              priceTrendsContext={priceTrendsContext}
              offerButton={
                shouldShowPriceTrendButton ? (
                  <PriceTrendOfferButton
                    messageKey={buttonContent.messageKey}
                    priceValue={buttonContent.priceValue}
                    currency={buttonContent.currency}
                    selectedOffer={selectedOffer!}
                    hotelId={hotelId}
                    onButtonClick={handlePriceTrendButtonClick}
                    onCTAClicked={trackCTAClicked}
                    isDesktop
                    size="xl"
                  />
                ) : null
              }
            />
          )}
        </Box>
      </div>
    </TeamContextProvider>
  )
}
